/**
 * Privacy-safe analytics bridge for host pages.
 *
 * The widget does not know which analytics provider a host uses. It emits a
 * browser event that the host page can listen for and forward to PostHog,
 * GA4, or its own tracking layer.
 */

import * as store from './store';

export type WidgetAnalyticsEvent =
  | 'chuck_widget_ready'
  | 'chuck_widget_preview_shown'
  | 'chuck_widget_preview_clicked'
  | 'chuck_widget_preview_dismissed'
  | 'chuck_widget_opened'
  | 'chuck_widget_closed'
  | 'chuck_session_initialized'
  | 'chuck_session_failed'
  | 'chuck_first_message_attempted'
  | 'chuck_starter_answer_clicked'
  | 'chuck_message_sent'
  | 'chuck_message_failed'
  | 'chuck_ai_response_received'
  | 'chuck_human_transfer_requested'
  | 'chuck_payment_link_shown'
  | 'chuck_sse_connected'
  | 'chuck_sse_error'
  | 'chuck_conversation_started'
  | 'chuck_conversation_switched';

export type WidgetAnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

export interface WidgetAnalyticsDetail extends WidgetAnalyticsProperties {
  event: WidgetAnalyticsEvent;
  project_id?: string;
  visitor_id?: string | null;
  conversation_id?: string | null;
  channel: 'webchat';
  widget_source: 'chuck_widget';
}

export const WIDGET_ANALYTICS_EVENT = 'chuck:analytics';

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function emitWidgetAnalytics(
  event: WidgetAnalyticsEvent,
  properties: WidgetAnalyticsProperties = {}
): void {
  if (typeof window === 'undefined') return;

  const projectId = stringOrNull(properties.project_id);
  const visitorId = stringOrNull(properties.visitor_id) ?? store.visitorId.value;
  const conversationId = stringOrNull(properties.conversation_id) ?? store.conversationId.value;

  const detail: WidgetAnalyticsDetail = {
    ...properties,
    event,
    channel: 'webchat',
    widget_source: 'chuck_widget',
    ...(projectId ? { project_id: projectId } : {}),
    visitor_id: visitorId,
    conversation_id: conversationId,
  };

  window.dispatchEvent(new CustomEvent<WidgetAnalyticsDetail>(WIDGET_ANALYTICS_EVENT, { detail }));
}

export function isPaymentLink(text: string | undefined | null): boolean {
  return Boolean(text && /https?:\/\/pay\.hellogravel\.com\/[^\s)]+/i.test(text));
}
